/**
 * Community Provider Type → Care Services deconflation (product 2026-08-25).
 *
 * `employment_services_provider` is a search-only *intent* slug, not a stored
 * facility identity. MN inventory lives on `home_and_community_based_services`
 * with employment capability keys. Provider Type dropdown keeps HCBS only;
 * Care Services → Employment & Vocational holds the filters, with a browse
 * link from Case Management / Care Coordination Provider Type help.
 *
 * Persisted URL / saved-search values that still carry the retired Employment
 * Provider Type are migrated to HCBS identity + required employment keys (BE
 * ORs within the employment group). Adult Foster Care remains a distinct
 * business type; its legacy spellings are normalized to the display label.
 *
 * When employment keys are present without a Provider Type, we fill it with
 * HCBS so the search uses the appropriate care setting. A Preferred selection
 * remains Preferred; normalization must never silently turn a ranking signal
 * into a hard eligibility constraint.
 *
 * Backend still translates the slug for direct API callers
 * (`translate_community_provider_business_type`).
 */

import type { SearchFilters } from "../types/providerSearch";
import {
  COMMUNITY_SERVICE_DEFINITIONS,
  BUSINESS_TYPE_DEFINITIONS,
} from "../generated/careTaxonomy";
import { resolveSearchBusinessTypeSlug } from "./caseTypeToBusinessType";

export const EMPLOYMENT_PROVIDER_TYPE_SLUG = "employment_services_provider";
export const HCBS_BUSINESS_TYPE_SLUG = "home_and_community_based_services";
export const ADULT_FOSTER_CARE_BUSINESS_TYPE_SLUG = "adult_family_home";

const EMPLOYMENT_GROUP = "employment_vocational_services";
const ADULT_FOSTER_CARE_VALUES = new Set([
  "adult_family_home",
  "adult_foster_care",
  "adultfamilyhome",
  "adultfostercare",
  "adultfostercarehome",
]);

/** Canonical employment Care Services keys (taxonomy group). */
export function employmentCommunityServiceKeys(): string[] {
  return COMMUNITY_SERVICE_DEFINITIONS.filter(
    (item) => item.group === EMPLOYMENT_GROUP
  ).map((item) => item.key);
}

function employmentKeySet(): Set<string> {
  return new Set(employmentCommunityServiceKeys());
}

function hcbsSearchFilterLabel(): string {
  const def = BUSINESS_TYPE_DEFINITIONS.find(
    (item) => item.key === HCBS_BUSINESS_TYPE_SLUG
  );
  return def?.searchFilterLabel ?? "Home & Community Based Services";
}

/** True for canonical or legacy Adult Foster Care search values. */
export function isAdultFosterCareFilterValue(
  value: string | null | undefined
): boolean {
  const normalizedValue = value?.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return (
    resolveSearchBusinessTypeSlug(value) ===
      ADULT_FOSTER_CARE_BUSINESS_TYPE_SLUG ||
    (normalizedValue !== undefined &&
      ADULT_FOSTER_CARE_VALUES.has(normalizedValue))
  );
}

/** Preserve Adult Foster Care intent while canonicalizing older URL spellings. */
export function normalizeAdultFosterCareFilterLabel(
  filters: SearchFilters
): SearchFilters {
  const businessType = filters.business_type?.trim();
  if (!businessType || !isAdultFosterCareFilterValue(businessType)) {
    return filters;
  }
  return {
    ...filters,
    business_type: "Adult Foster Care",
  };
}

/**
 * Provider Type dropdown value for HCBS (matches `PROVIDER_TYPES` option values,
 * which use searchFilterLabel — not the canonical slug).
 */
export function getHcbsProviderTypeFilterValue(): string {
  return hcbsSearchFilterLabel();
}

/**
 * True when `business_type` is the retired Employment Provider Type (slug or label).
 */
export function isEmploymentProviderTypeFilterValue(
  value: string | null | undefined
): boolean {
  const slug = resolveSearchBusinessTypeSlug(value);
  return slug === EMPLOYMENT_PROVIDER_TYPE_SLUG;
}

/**
 * Move Employment Provider Type intent into HCBS + required employment keys.
 */
export function migrateEmploymentProviderTypeToCareServices(
  filters: SearchFilters
): SearchFilters {
  const businessType = filters.business_type?.trim();
  if (!businessType || !isEmploymentProviderTypeFilterValue(businessType)) {
    return filters;
  }

  const employmentKeys = employmentCommunityServiceKeys();
  const existingRequired = filters.required_service_keys ?? [];
  const requiredSet = new Set(existingRequired);
  for (const key of employmentKeys) {
    requiredSet.add(key);
  }

  return {
    ...filters,
    business_type: hcbsSearchFilterLabel(),
    required_service_keys: [...requiredSet],
  };
}

/**
 * Employment Care Services without a Provider Type use the HCBS care setting.
 * Preserve each service's Required or Preferred strength.
 */
export function normalizeEmploymentCareServicesForSearch(
  filters: SearchFilters
): SearchFilters {
  const empKeys = employmentKeySet();
  if (empKeys.size === 0) {
    return filters;
  }

  const preferred = filters.preferred_service_keys ?? [];
  const required = filters.required_service_keys ?? [];
  const preferredEmployment = preferred.filter((key) => empKeys.has(key));
  const requiredEmployment = required.filter((key) => empKeys.has(key));
  const hasEmploymentIntent =
    preferredEmployment.length > 0 || requiredEmployment.length > 0;

  if (!hasEmploymentIntent) {
    return filters;
  }

  let next: SearchFilters = filters;

  const businessType = next.business_type?.trim();
  if (
    !businessType ||
    isEmploymentProviderTypeFilterValue(businessType)
  ) {
    next = {
      ...next,
      business_type: hcbsSearchFilterLabel(),
    };
  }

  return next;
}
